import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api, type AppointmentPreset } from '@/lib/api'

interface PresetSelectProps {
  kind: 'note' | 'location'
  onSelect: (text: string) => void
}

/**
 * 预约表单预设选择控件（OPT-016）
 * - 触发按钮 → 下拉列表（预设项 + 管理入口）
 * - 管理入口 → 小弹窗就地增删
 * - 备注/地点复用，通过 kind 区分
 */
export function PresetSelect({ kind, onSelect }: PresetSelectProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['appointment-presets', kind],
    queryFn: () => api.getAppointmentPresets(kind),
    staleTime: 60 * 1000,
  })
  const presets = data?.items || []

  const handleSelect = (text: string) => {
    onSelect(text)
    setOpen(false)
  }

  const handleOpenManage = () => {
    setOpen(false)
    setManageOpen(true)
  }

  // 外部点击关闭（react 合成事件加延迟以跳过触发按钮的 toggle）
  const handleBlur = () => {
    setTimeout(() => {
      if (manageOpen) return // 管理弹窗开着时不下拉关闭
      setOpen(false)
    }, 150)
  }

  return (
    <div className="relative" ref={triggerRef}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid={`preset-trigger-${kind}`}
        onClick={() => setOpen((o) => !o)}
        onBlur={handleBlur}
        className="text-xs text-slate-500 hover:text-slate-700"
      >
        {t('schedule.presets.trigger')}
      </Button>

      {open && (
        <div
          data-testid={`preset-list-${kind}`}
          className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border bg-white shadow-lg"
        >
          {presets.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-400">{t('schedule.presets.empty')}</p>
          ) : (
            <ul className="max-h-48 overflow-auto py-1">
              {presets.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    data-testid={`preset-item-${kind}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                    onMouseDown={(e) => {
                      e.preventDefault() // 防止 blur 先于 click 触发
                      handleSelect(p.text)
                    }}
                  >
                    {p.text}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t">
            <button
              type="button"
              data-testid={`preset-manage-${kind}`}
              className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
              onMouseDown={(e) => {
                e.preventDefault()
                handleOpenManage()
              }}
            >
              {t('schedule.presets.manage')}
            </button>
          </div>
        </div>
      )}

      <PresetManageDialog
        kind={kind}
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        presets={presets}
      />
    </div>
  )
}

/** 管理弹窗：列表增删 */
function PresetManageDialog({
  kind,
  open,
  onClose,
  presets,
}: {
  kind: 'note' | 'location'
  open: boolean
  onClose: () => void
  presets: AppointmentPreset[]
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [addText, setAddText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ['appointment-presets', kind] })
  }

  const handleAdd = async () => {
    const trimmed = addText.trim()
    if (!trimmed) {
      setError(t('schedule.presets.addPlaceholder'))
      return
    }
    setAdding(true)
    setError(null)
    try {
      await api.createAppointmentPreset(kind, trimmed)
      setAddText('')
      refetch()
    } catch (e: any) {
      setError(e?.message || t('common.failed'))
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await api.deleteAppointmentPreset(id)
      refetch()
    } catch (e: any) {
      setError(e?.message || t('common.failed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent data-testid="preset-manage-dialog">
        <DialogHeader>
          <DialogTitle>{t('schedule.presets.manageTitle')}</DialogTitle>
        </DialogHeader>
        <ul className="max-h-48 overflow-auto space-y-1">
          {presets.length === 0 && (
            <p className="text-xs text-slate-400">{t('schedule.presets.empty')}</p>
          )}
          {presets.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-slate-50">
              <span>{p.text}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`preset-delete-${p.id}`}
                onClick={() => handleDelete(p.id)}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                {t('common.delete')}
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2 mt-2">
          <Input
            data-testid="preset-add-input"
            value={addText}
            onChange={(e) => {
              setAddText(e.target.value)
              if (error) setError(null)
            }}
            placeholder={t('schedule.presets.addPlaceholder')}
            maxLength={200}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd() } }}
          />
          <Button
            type="button"
            data-testid="preset-add-submit"
            onClick={handleAdd}
            disabled={adding}
            size="sm"
          >
            {adding ? t('common.saving') : t('schedule.presets.add')}
          </Button>
        </div>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
